import { Logout as LogoutIcon, Person as PersonIcon } from '@mui/icons-material'
import { AppBar, Hidden, Stack, Tooltip, useMediaQuery } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import { Box, useTheme } from '@mui/system'
import { signIn } from 'next-auth/react'
import Image from 'next/image'
import { useRouter } from 'next/router'
import ChatButtonContainer from '../features/Chat/containers/ChatButtonContainer'
import CountryFilterContainer from '../features/countryFilter/containers/CountryFilterContainer'
import NotificationButtonContainer from '../features/Chat/containers/NotificationButtonContainer'
import LiveCallMonitorButtonContainer from '../features/liveCallMonitor/containers/LiveCallMonitorButtonContainer'
import useLocalization from '../features/localization/hooks/useLocalization'
import ReadingPlanStatusContainer from '../features/readingPlan/containers/ReadingPlanStatusContainer'
import { useGetReadingPlanForUserQuery } from '../generated/graphql'
import useAuthClaims from '../hooks/useAuthClaims'
import { handleSignOut } from '../lib/auth0'
import { HLO, UserMenuItemType } from '../types/global.d'
import HeaderDivider from './HeaderDivider'
import HeaderMenu from './HeaderMenu'
import Logo from './Logo'
import PageContainer from './PageContainer'
import SkipToMain from './SkipToMain'
import UserBadge from './UserBadge'
import UserMenu, { UserMenuItem } from './UserMenu'
import { useTranslation } from 'next-i18next'
import LanguageSwitcher from './LanguageSwitcher'
import React from 'react'

const Header = () => {
  const { claims, loading, userId, countryISO } = useAuthClaims()
  const { language } = useLocalization()
  const role = claims?.role || ''
  const router = useRouter()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const isMedium = useMediaQuery(theme.breakpoints.down('md'))
  const isLarge = useMediaQuery(theme.breakpoints.down('lg'))
  const isNarrow = useMediaQuery('(max-width:796px)')
  const isReadingPlanOverflow = useMediaQuery('(max-width:1137px)')
  const { t } = useTranslation('HeaderMenu')

  const queries = {
    readingPlans: useGetReadingPlanForUserQuery({
      skip: !language || !userId || !['reader'].includes(role),
      variables: { languageId: language?.id, userId: userId as string },
    }),
  }

  const readingPlan =
    queries.readingPlans.data &&
    Array.isArray(queries.readingPlans.data.readingPlans) &&
    queries.readingPlans.data.readingPlans.length > 0
      ? queries.readingPlans.data.readingPlans[0]
      : undefined
  const remainingReadings = readingPlan ? readingPlan?.remainingreadings : 0
  const readingPlanExpireDate = readingPlan
    ? new Date(readingPlan.valid_to)
    : undefined

  const showMenuItems = router.pathname.indexOf('/onboarding') === -1

  const handleMenuItemClick = ({ onClick, type, value }: UserMenuItem) => {
    if (type === UserMenuItemType.LINK && typeof value === 'string') {
      router.push(value)
    }

    if (type === UserMenuItemType.BUTTON && typeof onClick === 'function') {
      onClick()
    }
  }

  const showReadingPlanStatus =
    typeof remainingReadings === 'number' &&
    !!readingPlanExpireDate &&
    !!language
  const maxVisibleItems = isNarrow
    ? 0
    : isMedium
      ? 2
      : showReadingPlanStatus && isReadingPlanOverflow
        ? 2
        : showReadingPlanStatus
          ? isLarge
            ? 5
            : 4
          : 4

  const getUserMenuItems = (userRole: HLO.Role) => {
    const userMenuItems: UserMenuItem[] = []

    if (
      ['hlo-admin', 'librarian', 'book', 'reader', 'superreader'].includes(
        userRole,
      )
    ) {
      userMenuItems.push({
        icon: <PersonIcon />,
        label: t('my profile'),
        type: UserMenuItemType.LINK,
        value: '/profile',
      })
    }

    userMenuItems.push({
      icon: <LogoutIcon />,
      label: t('log out'),
      type: UserMenuItemType.BUTTON,
      onClick: () => handleSignOut(),
    })

    return userMenuItems
  }

  const actions = [
    claims ? (
      <Stack
        alignItems="center"
        direction="row"
        key="HeaderAction_Logout"
        spacing="16px"
        flexWrap="wrap"
      >
        {showReadingPlanStatus && (
          <Hidden mdDown>
            <ReadingPlanStatusContainer />
            <HeaderDivider />
          </Hidden>
        )}

        <Stack alignItems="center" direction="row" spacing="12px">
          <CountryFilterContainer />
          {claims && !['guest', 'local-librarian'].includes(role) && (
            <ChatButtonContainer />
          )}
          {claims && ['hlo-admin', 'librarian'].includes(role) && (
            <NotificationButtonContainer />
          )}
          {claims && ['hlo-admin', 'librarian'].includes(role) && (
            <LiveCallMonitorButtonContainer />
          )}

          <UserMenu
            items={loading ? [] : getUserMenuItems(claims?.role)}
            onMenuItemClick={handleMenuItemClick}
          >
            <UserBadge name={claims.name || ''} />
          </UserMenu>
        </Stack>
      </Stack>
    ) : (
      <React.Fragment key="HeaderAction_Login">
        <Tooltip title={t('log in')}>
          <IconButton onClick={() => signIn('auth0')}>
            <UserBadge />
          </IconButton>
        </Tooltip>
      </React.Fragment>
    ),
  ]

  return (
    <AppBar role="header" position="relative" component="nav">
      <PageContainer disableGutters>
        <SkipToMain />
        {isSmall ? (
          <Stack
            direction="row"
            paddingX="16px"
            height="68px"
            alignItems="center"
            width="100%"
            justifyContent="space-between"
          >
            <Logo role={role} isoCode={countryISO} />

            {!loading &&
              [
                'reader',
                'hlo-admin',
                'librarian',
                'book',
                'local-librarian',
              ].includes(role || '') && (
                <Box
                  sx={{
                    width: 'fit-content',
                  }}
                >
                  <HeaderMenu
                    selected={router.pathname}
                    showMenuItems={showMenuItems}
                    role={claims?.role}
                    countryISO={claims?.countryISO ?? ''}
                    maxVisibleItems={maxVisibleItems}
                  />
                </Box>
              )}

            <Stack direction="row" alignItems="center" flexWrap="wrap">
              {actions}
            </Stack>
          </Stack>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            height="68px"
            paddingX="16px"
            width="100%"
            sx={{
              overflowX: 'hidden',
              flexWrap: 'nowrap',
              justifyContent: 'space-between',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Logo role={role} isoCode={countryISO} />
              {['hlo-admin'].includes(role) && (
                <Box sx={{ display: 'flex', maxWidth: 52.7, minWidth: 52.7 }}>
                  <Image
                    alt={t('The Human Library Logo')}
                    height={23.5}
                    width={52.7}
                    objectFit="contain"
                    src="/Admin.svg"
                    unoptimized={process.env.NODE_ENV === 'development'}
                  />
                </Box>
              )}
              {!loading &&
                [
                  'reader',
                  'hlo-admin',
                  'librarian',
                  'book',
                  'local-librarian',
                ].includes(role || '') && (
                  <Box
                    sx={{
                      paddingLeft: '24px',
                      maxWidth: '700px',
                      flexShrink: 0, // prevent shrinking
                      flexGrow: 1,
                      overflowX: 'auto',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <HeaderMenu
                      selected={router.pathname}
                      showMenuItems={showMenuItems}
                      role={claims?.role}
                      countryISO={claims?.countryISO ?? ''}
                      maxVisibleItems={maxVisibleItems}
                    />
                  </Box>
                )}
            </Stack>

            <Stack direction="row" alignItems="center" spacing={2}>
              {!loading && actions}
            </Stack>
          </Stack>
        )}
      </PageContainer>
    </AppBar>
  )
}

export default Header
